import Button from "@components/Button"
import TermsCheckbox from "@components/TermsCheckbox"
import { CURRENT_CONFIDENCE_FLAG, getVariant } from "@helpers/confidence"
import {
  resolveConfidenceVisitorId,
  VISITOR_ID_QUERY_PARAM,
} from "@helpers/confidenceVisitorId"
import { trackSubscriptionEvent } from "@helpers/facebookTracking"
import localizeCurrency from "@helpers/localizeCurrency"
import localizeDate from "@helpers/localizeDate"
import * as Sentry from "@sentry/react"
import { useFlag } from "@spotify-confidence/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { PaymentIntent } from "@stripe/stripe-js"
import { addWeeks, addYears } from "date-fns"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { Plan } from "src/types"

interface Props {
  clientSecret: string
  currency: string
  fullPrice: number
  plan: Plan
  stripeFirstPriceId: string
  stripeNextPriceId: string
  subscriptionId: string
  userEmail: string | undefined
}

export default function CheckoutForm({
  clientSecret,
  fullPrice,
  plan,
  subscriptionId,
  stripeFirstPriceId,
  stripeNextPriceId,
  userEmail,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | undefined>(
    undefined,
  )

  const elements = useElements()
  const location = useLocation()
  const stripe = useStripe()
  const { t } = useTranslation()

  const flag = useFlag(CURRENT_CONFIDENCE_FLAG, { variant: "baseline" })
  const variant = getVariant(flag)

  const paymentMethodOrder =
    variant === "v1"
      ? ["apple_pay", "card", "paypal", "google_pay"]
      : ["card", "paypal", "apple_pay", "google_pay"]

  const getPrice = () =>
    localizeCurrency({
      amountInCents: paymentIntent?.amount,
      currency: paymentIntent?.currency,
    })

  const renewalDate: Date =
    plan === "weekly" ? addWeeks(new Date(), 1) : addYears(new Date(), 1)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!elements || !stripe) return

    setIsSubmitting(true)

    const origin = window.location.origin
    const pathname = "/checkout/payment-completion"
    const urlSearchParams = new URLSearchParams(location.search)

    urlSearchParams.append("plan", plan)
    urlSearchParams.append("stripe_first_price_id", stripeFirstPriceId)
    urlSearchParams.append("stripe_next_price_id", stripeNextPriceId)
    urlSearchParams.append("subscription_id", subscriptionId)

    // Confirming payment hands the browser to Stripe, and coming back is a
    // fresh page load that has to find the visitor id again. Measured on Lingo
    // web, 23% of purchases find nothing there and mint a new id, so the
    // purchase lands on a visitor with no funnel behind it. Passing it through
    // the return URL is the only carrier the browser cannot drop.
    //
    // Cleared before writing, and written with `set` rather than `append`,
    // because these params start as a copy of the current URL's. A visitor
    // arriving on an attacker-crafted `?cnfd_visitor_id=` would otherwise carry
    // it into the return URL, and since `URLSearchParams.get` returns the first
    // of a duplicated key, theirs would win over ours on the way back. The
    // delete also covers the case where we have no id to send: an inbound one
    // must not survive on its own.
    urlSearchParams.delete(VISITOR_ID_QUERY_PARAM)

    const confidenceVisitorId = resolveConfidenceVisitorId()
    if (confidenceVisitorId) {
      urlSearchParams.set(VISITOR_ID_QUERY_PARAM, confidenceVisitorId)
    }

    const returnUrl = `${origin}${pathname}?${urlSearchParams.toString()}`

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })

    setIsSubmitting(false)

    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message)
    } else {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw error
    }
  }

  useEffect(() => {
    if (!clientSecret || !stripe) {
      return
    }

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        setPaymentIntent(paymentIntent)

        if (paymentIntent?.last_payment_error) {
          if (
            paymentIntent.last_payment_error.code ===
            "payment_intent_authentication_failure"
          ) {
            setMessage(t("components.checkoutForm.paymentFailed"))
          } else {
            setMessage(t("components.checkoutForm.somethingWentWrong"))
          }
        }
      })
      .catch((error) => {
        throw error
      })
  }, [clientSecret, setPaymentIntent, stripe, t, plan])

  useEffect(() => {
    if (!paymentIntent) return

    const trackEvent = async () => {
      await trackSubscriptionEvent({
        currency: paymentIntent.currency,
        isPurchase: false,
        planPrice: paymentIntent.amount,
        stripeFirstPriceId,
        stripeNextPriceId,
      })
    }

    trackEvent().catch((error) => Sentry.captureException(error))
  }, [paymentIntent, stripeFirstPriceId, stripeNextPriceId])

  useEffect(() => {
    const resetSubmittingState = () => {
      setIsSubmitting(false)
    }

    window.addEventListener("pageshow", resetSubmittingState)
    document.addEventListener("visibilitychange", resetSubmittingState)

    return () => {
      window.removeEventListener("pageshow", resetSubmittingState)
      document.removeEventListener("visibilitychange", resetSubmittingState)
    }
  }, [])

  return (
    <form
      className="flex grow flex-col justify-between gap-8"
      onSubmit={handleSubmit}
    >
      <div className="rounded-2xl border border-custom-gray bg-white p-5">
        <PaymentElement
          options={{
            defaultValues: {
              billingDetails: {
                email: userEmail,
              },
            },
            layout: {
              defaultCollapsed: false,
              type: "tabs",
            },
            paymentMethodOrder,
          }}
        />
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-bold">
          {t("pages.checkout.payment.orderSummary.heading")}
        </h2>

        <div className="space-y-4 rounded-2xl border border-custom-gray bg-white p-5">
          <div className="space-y-2 text-sm">
            <p className="font-bold">
              {t("pages.checkout.payment.orderSummary.fullaccessHeadline")}
            </p>
            <p>{t("pages.checkout.payment.orderSummary.fullaccessContent")}</p>
          </div>

          <div className="space-y-2 text-sm">
            <p className="font-bold">
              {t("pages.checkout.payment.orderSummary.durationHeadline")}
            </p>

            <p>
              {plan === "weekly"
                ? t("pages.checkout.payment.orderSummary.durationWeek", {
                    count: 1,
                  })
                : t("pages.checkout.payment.orderSummary.durationYear", {
                    count: 1,
                  })}
            </p>
          </div>

          <div className="space-y-2 text-sm">
            <p className="font-bold">
              {t("pages.checkout.payment.orderSummary.renewalHeadline")}
            </p>

            <p>{localizeDate(renewalDate)}</p>

            <p>{t("pages.checkout.payment.orderSummary.renewalDisclaimer")}</p>
          </div>

          {paymentIntent && (
            <div className="space-y-2">
              {paymentIntent.amount !== fullPrice ? (
                <>
                  <p className="line-through">
                    {t(
                      `pages.checkout.payment.${plan === "weekly" ? "thisWeek" : "thisYear"}`,
                      {
                        price: localizeCurrency({
                          amountInCents: fullPrice,
                          currency: paymentIntent.currency,
                        }),
                      },
                    )}
                  </p>

                  <p className="font-bold">
                    {t(
                      plan === "weekly"
                        ? "pages.checkout.payment.firstWeekSpecialOffer"
                        : "pages.checkout.payment.firstYearSpecialOffer",
                      { price: getPrice() },
                    )}
                  </p>
                </>
              ) : (
                <p className="font-bold">
                  {t(
                    `pages.checkout.payment.${plan === "weekly" ? "thisWeek" : "thisYear"}`,
                    { price: getPrice() },
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <TermsCheckbox
          fullPrice={fullPrice}
          isTermsAccepted={isTermsAccepted}
          paymentIntent={paymentIntent}
          plan={plan}
          setIsTermsAccepted={setIsTermsAccepted}
        />

        {message && (
          <p className="custom-text-error mx-auto w-3/4 rounded-2xl border border-custom-error bg-white p-4 text-center text-lg font-semibold">
            {message}
          </p>
        )}

        <Button
          disabled={!elements || !stripe || !isTermsAccepted || isSubmitting}
          title={t("pages.checkout.payment.submitPayment")}
          type="submit"
        />

        <p className="text-center text-xs">
          {t("pages.checkout.payment.termsCheckboxHint")}
        </p>
      </div>
    </form>
  )
}
